<script lang="ts" setup>
import { reactive, ref, onMounted } from 'vue'
import { useUserStore } from '../../stores/user/user.store'
import jsonpatch, { type Observer } from 'fast-json-patch'
import type { UserProfileDetailsDto } from '@roadfootage/api-client'
import * as limits from '../../utils/limits'
import { isRequired, hasMaxLength } from '../../utils/validation'
import { regionConfig } from '@/config/region'

const emit = defineEmits(['save', 'reset'])
const userStore = useUserStore()
const savingProfileDetails = ref(false)
const profileDetailsForm: UserProfileDetailsDto = reactive({
  countryCode: regionConfig.countryCode,
  name: '',
  givenName: '',
  familyName: '',
  nickname: '',
  picture: '',
  phoneNumber: ''
})

const formRef = ref()

// Helper for exact length
const hasExactLength = (len: number, message: string) => (val: string) => {
  return !val || val.length === len || message
}
let patchObserver: Observer<UserProfileDetailsDto>

async function onSubmit() {
  // Validate form before submit
  const valid = await formRef.value?.validate?.()
  if (!valid) return
  savingProfileDetails.value = true
  profileDetailsForm.name = `${profileDetailsForm.givenName || ''} ${
    profileDetailsForm.familyName || ''
  }`.trim()
  const patchOperations = jsonpatch.generate<UserProfileDetailsDto>(patchObserver)
  try {
    await userStore.updateProfile(patchOperations)
  } catch (e) {
    console.error(e)
    return
  } finally {
    savingProfileDetails.value = false
  }
  emit('save')
}

onMounted(() => {
  const profile = userStore.userProfile!
  profileDetailsForm.countryCode = profile.countryCode
  profileDetailsForm.givenName = profile.givenName
  profileDetailsForm.familyName = profile.familyName
  profileDetailsForm.phoneNumber = profile.phoneNumber
  patchObserver = jsonpatch.observe(profileDetailsForm)
})
</script>

<template>
  <q-form ref="formRef" @submit="onSubmit" @reset="emit('reset')" class="q-gutter-md">
    <q-input
      v-model="profileDetailsForm.countryCode"
      label="Country Code *"
      lazy-rules
      :rules="[
        isRequired('Country code is required.'),
        hasExactLength(
          limits.countryCode,
          `Country code must be exactly ${limits.countryCode} characters.`
        )
      ]"
      outlined
    />
    <q-input
      v-model="profileDetailsForm.givenName"
      label="Given name *"
      lazy-rules
      :rules="[
        isRequired('Please enter your given name.'),
        hasMaxLength(limits.givenName, `Given name must not exceed ${limits.givenName} characters.`)
      ]"
      outlined
    />
    <q-input
      v-model="profileDetailsForm.familyName"
      label="Family name *"
      lazy-rules
      :rules="[
        isRequired('Please enter your family name.'),
        hasMaxLength(
          limits.familyName,
          `Family name must not exceed ${limits.familyName} characters.`
        )
      ]"
      outlined
    />
    <q-input
      v-model="profileDetailsForm.nickname"
      label="Nickname"
      lazy-rules
      :rules="[
        hasMaxLength(limits.nickname, `Nickname must not exceed ${limits.nickname} characters.`)
      ]"
      outlined
    />
    <q-input
      v-model="profileDetailsForm.picture"
      label="Picture URL"
      lazy-rules
      :rules="[hasMaxLength(limits.url, `Picture URL must not exceed ${limits.url} characters.`)]"
      outlined
    />
    <q-input
      v-model="profileDetailsForm.phoneNumber"
      label="Phone Number"
      lazy-rules
      :rules="[
        hasMaxLength(
          limits.phoneNumber,
          `Phone number must not exceed ${limits.phoneNumber} characters.`
        )
      ]"
      outlined
    />
    <q-btn
      label="Submit"
      type="submit"
      color="primary"
      :disable="savingProfileDetails"
      :loading="savingProfileDetails"
    />
    <q-btn label="Cancel" type="reset" flat class="q-ml-sm" :disable="savingProfileDetails" />
  </q-form>
</template>
